import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Minus, Plus, MapPin, Check, CloudOff, Loader2 } from 'lucide-react'
import { createKraalObservation } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'

// Offline-first kraal census (Session 4). A pure capture form — no data reads —
// so it works fully offline: the herder tallies the herd by type at the kraal,
// the write queues on the device and syncs idempotently on reconnect.
// Phones can't read LF cattle RFID, so this is manual counts; per-tag entry and
// photos come later (photos need an online upload).

const CATEGORIES = [
  { key: 'bulls_count', label: 'Bulls' },
  { key: 'cows_count', label: 'Cows' },
  { key: 'heifers_count', label: 'Heifers' },
  { key: 'weaners_count', label: 'Weaners' },
  { key: 'calves_count', label: 'Calves' },
  { key: 'steers_count', label: 'Steers' },
]

const today = () => new Date().toISOString().slice(0, 10)
const zeroCounts = () => CATEGORIES.reduce((acc, c) => ({ ...acc, [c.key]: 0 }), {})

export default function CensusCapture({ ranchId }) {
  const { userId } = useAuth()
  const [date, setDate] = useState(today())
  const [counts, setCounts] = useState(zeroCounts)
  const [notes, setNotes] = useState('')
  const [gps, setGps] = useState(null)
  const [gpsState, setGpsState] = useState('idle') // idle | locating | done | error
  const [result, setResult] = useState(null) // { queued } after a successful save

  const total = CATEGORIES.reduce((sum, c) => sum + (counts[c.key] || 0), 0)

  const bump = (key, delta) =>
    setCounts((c) => ({ ...c, [key]: Math.max(0, (c[key] || 0) + delta) }))

  const captureGps = () => {
    if (!('geolocation' in navigator)) { setGpsState('error'); return }
    setGpsState('locating')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude })
        setGpsState('done')
      },
      () => setGpsState('error'),
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  const mutation = useMutation({
    mutationFn: createKraalObservation,
    onSuccess: (res) => {
      setResult({ queued: !!res?._offline_queued })
      // Reset for the next count; keep the date.
      setCounts(zeroCounts())
      setNotes('')
      setGps(null)
      setGpsState('idle')
    },
  })

  const submit = () => {
    setResult(null)
    mutation.mutate({
      ranch_id: ranchId,
      observation_date: date,
      location_type: 'kraal',
      observation_method: 'manual_count',
      observer_id: userId || undefined,
      total_count: total,
      ...counts,
      location_description: gps ? `GPS ${gps.lat.toFixed(5)}, ${gps.lng.toFixed(5)}` : undefined,
      notes: notes.trim() || undefined,
    })
  }

  return (
    <div className="max-w-xl mx-auto pb-28">
      <div className="mb-4">
        <h1 className="text-2xl font-display font-bold text-primary-900">Kraal Census</h1>
        <p className="text-earth-600 text-sm mt-1">
          Tally the herd by type. Works offline — it saves on your phone and syncs when you reconnect.
        </p>
      </div>

      {result && (
        <div className={`rounded-xl p-4 mb-4 flex items-center gap-3 ${
          result.queued ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'
        }`}>
          {result.queued ? <CloudOff className="h-5 w-5 shrink-0" /> : <Check className="h-5 w-5 shrink-0" />}
          <span className="text-sm font-medium">
            {result.queued
              ? 'Saved on your phone — it will sync automatically when you reconnect.'
              : 'Census recorded.'}
          </span>
        </div>
      )}

      <label className="block mb-4">
        <span className="text-sm font-medium text-earth-700">Date</span>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="mt-1 w-full rounded-lg border border-earth-300 px-3 py-3 text-lg"
        />
      </label>

      <div className="space-y-3">
        {CATEGORIES.map((c) => (
          <div key={c.key} className="flex items-center justify-between rounded-xl border border-earth-200 bg-white p-3">
            <span className="text-lg font-medium text-primary-900 pl-1">{c.label}</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => bump(c.key, -1)}
                aria-label={`Decrease ${c.label}`}
                disabled={counts[c.key] === 0}
                className="h-12 w-12 rounded-full bg-earth-100 text-earth-700 flex items-center justify-center active:bg-earth-200 disabled:opacity-40"
              >
                <Minus className="h-6 w-6" />
              </button>
              <span className="w-10 text-center text-2xl font-bold tabular-nums">{counts[c.key]}</span>
              <button
                type="button"
                onClick={() => bump(c.key, 1)}
                aria-label={`Increase ${c.label}`}
                className="h-12 w-12 rounded-full bg-primary-600 text-white flex items-center justify-center active:bg-primary-700"
              >
                <Plus className="h-6 w-6" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={captureGps}
        className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl border border-earth-300 py-3 text-earth-700 active:bg-earth-100"
      >
        <MapPin className="h-5 w-5" />
        {gpsState === 'locating' ? 'Getting location…'
          : gpsState === 'done' ? `Location captured (${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)})`
          : gpsState === 'error' ? 'Location unavailable — tap to retry'
          : 'Capture location'}
      </button>

      <label className="block mt-4">
        <span className="text-sm font-medium text-earth-700">Notes (optional)</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="mt-1 w-full rounded-lg border border-earth-300 px-3 py-2"
          placeholder="Anything unusual?"
        />
      </label>

      {mutation.isError && (
        <p className="mt-3 text-sm text-red-600">
          Couldn&apos;t save: {mutation.error?.response?.data?.detail || mutation.error?.message || 'please try again'}
        </p>
      )}

      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-earth-200 p-4 z-20">
        <div className="max-w-xl mx-auto flex items-center gap-4">
          <div className="flex-1">
            <div className="text-xs text-earth-500 uppercase tracking-wide">Total head</div>
            <div className="text-3xl font-bold text-primary-900 tabular-nums">{total}</div>
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={total === 0 || mutation.isPending}
            className="btn btn-primary px-8 py-4 text-lg disabled:opacity-50 flex items-center gap-2"
          >
            {mutation.isPending && <Loader2 className="h-5 w-5 animate-spin" />}
            {mutation.isPending ? 'Saving…' : 'Save census'}
          </button>
        </div>
      </div>
    </div>
  )
}
